/**
 * The one map between a tab key and a URL — and the reason phase 4 happened.
 *
 * ── Why tab keys did not simply become paths ────────────────────────────────
 *
 * The app navigated by a free-form string (`navigate('supplier360', {…})`) held
 * in React state, and those keys are load-bearing in three other places: the
 * PAGES registry, Layout's nav highlighting, and the Copilot's per-page
 * SUGGESTIONS. Renaming them to suit URLs would have rippled through all three
 * for no gain, so the keys are unchanged and this file is the only thing that
 * knows what a key looks like in the address bar.
 *
 * ⚠ Two of them are NOT their key, and it matters:
 *   `contracts-register` (the contract REGISTER) is `/contracts`
 *   `contracts`          (contract COVERAGE)     is `/coverage`
 * Those keys read backwards — a historical accident — and the whole point of
 * this phase is that a contract is linkable, so the register had to own
 * `/contracts` and therefore `/contracts/:ref`. Coverage takes the name it
 * actually has. Nothing outside this file needs to know.
 */

/** tab key → URL path (no leading slash on the child segment). */
export const PATH_BY_TAB: Record<string, string> = {
  spend: 'spend',
  price: 'price',
  brief: 'brief',
  relationships: 'relationships',
  supplier360: 'supplier360',
  performance: 'performance',
  register: 'price-register',
  playbooks: 'playbooks',
  pipeline: 'pipeline',
  'contracts-register': 'contracts',
  contracts: 'coverage',
  governance: 'governance',
  'data-sources': 'data-sources',
};

/** URL path → tab key. Built from the map above so the two cannot drift. */
export const TAB_BY_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(PATH_BY_TAB).map(([tab, path]) => [path, tab]),
);

/** The tab shown when the URL names nothing we recognise, and the redirect
 *  target for `/`. Same fallback the pre-router App used. */
export const DEFAULT_TAB = 'spend';

/**
 * The deep link this phase exists to deliver: a contract reference in the URL,
 * so a register row can be sent to somebody in an email or a task.
 *
 * `/contracts/VOD-2026-06` opens the register with that contract selected. The
 * ref is the human one printed on the row (`contractRef`), not the GUID — a URL
 * nobody can read is not a link anybody will send.
 *
 * ⚠ A ref can contain characters that need encoding, and the register also holds
 * rows whose ref is null (they render as "unref'd"). Those have no deep link, by
 * design: there is nothing stable to put in the URL.
 */
export const contractPath = (contractRef: string): string =>
  `/contracts/${encodeURIComponent(contractRef)}`;

/** Path for a tab, with the leading slash. */
export const pathForTab = (tab: string): string => `/${PATH_BY_TAB[tab] ?? PATH_BY_TAB[DEFAULT_TAB]}`;

/** Tab for a path segment, falling back the way the old registry did. */
export const tabForPath = (segment: string | undefined): string =>
  (segment && TAB_BY_PATH[segment]) || DEFAULT_TAB;
